import { defineStore } from 'pinia';
import commentApi from '@/api/commentApi.js';

function makeKey(target, id) {
    return `${target}:${id}`;
}

export const useCommentsStore = defineStore('commentsStore', {
    state: () => ({
        // threads: { 'customer:123': { count, results, loading, page } }
        threads: {},
    }),

    getters: {
        thread: (state) => (target, id) =>
            state.threads[makeKey(target, id)] || { count: 0, results: [], loading: false, page: 1 },
        hasMore: (state) => (target, id) => {
            const t = state.threads[makeKey(target, id)];
            if (!t) return false;
            const resultsLen = Array.isArray(t.results) ? t.results.length : 0;
            return resultsLen < Number(t.count || 0);
        },
    },

    actions: {
        // Полная загрузка страницы (перезапись результатов)
        async loadComments({ target, id, page = 1 }) {
            const key = makeKey(target, id);
            if (!this.threads[key]) this.threads[key] = { count: 0, results: [], loading: false, page: 1 };

            this.threads[key].loading = true;
            try {
                const data = await commentApi.list({ target, id, page });
                this.threads[key] = {
                    count: Number(data?.count ?? 0),
                    results: Array.isArray(data?.results) ? data.results : [],
                    loading: false,
                    page,
                };
                return this.threads[key];
            } catch (e) {
                this.threads[key].loading = false;
                throw e;
            }
        },

        // Догрузка следующей страницы (append)
        async loadMore({ target, id }) {
            const key = makeKey(target, id);
            const t = this.threads[key] || { count: 0, results: [], loading: false, page: 0 };
            // если уже грузим или догружать нечего — выходим
            const resultsLen = Array.isArray(t.results) ? t.results.length : 0;
            if (t.loading || resultsLen >= Number(t.count || 0)) return t;

            const nextPage = (t.page || 1) + 1;
            this.threads[key].loading = true;
            try {
                const data = await commentApi.list({ target, id, page: nextPage });
                const newResults = Array.isArray(data?.results) ? data.results : [];
                this.threads[key] = {
                    count: Number(data?.count ?? t.count ?? 0),
                    results: [...(t.results || []), ...newResults],
                    loading: false,
                    page: nextPage,
                };
                return this.threads[key];
            } catch (e) {
                this.threads[key].loading = false;
                throw e;
            }
        },

        async addComment({ target, id, body }) {
            const key = makeKey(target, id);
            const created = await commentApi.create({ body, target, id });
            if (!this.threads[key]) this.threads[key] = { count: 0, results: [], loading: false, page: 1 };
            this.threads[key].results = [{ ...created }, ...this.threads[key].results];
            this.threads[key].count = (this.threads[key].count || 0) + 1;
            return created;
        },

        async deleteComment({ target, id, commentId }) {
            const key = makeKey(target, id);
            await commentApi.remove(commentId);
            if (this.threads[key]?.results) {
                this.threads[key].results = this.threads[key].results.filter(c => c.id !== commentId);
                this.threads[key].count = Math.max(0, (this.threads[key].count || 0) - 1);
            }
            return true;
        },

        resetThread(target, id) {
            delete this.threads[makeKey(target, id)];
        },
    },
});